import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {useShopperCustomersMutation, useCustomerId, useCustomer} from '@salesforce/commerce-sdk-react'
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import Field from '../../components/field'
import {RightArrowWhite, CheckCircleIcon} from '../../components/icons'
import useCommunicationsFields from '../../components/forms/useCommunicationsFields'
import {useForm} from 'react-hook-form'
import {useBrand} from '../../contexts/brand-context'
import {useLegalPagePaths} from '../../hooks/use-legal-page-paths'

import {sendViewErrorEvent} from '../../utils/gtm-utils'

const SMS_OPT_IN_KEYS = {
    garage: 'c_sms_garage_optedIn',
    dynamite: 'c_sms_dynamite_optedIn'
}

const getSmsOptInKey = (isGarage) => (isGarage ? SMS_OPT_IN_KEYS.garage : SMS_OPT_IN_KEYS.dynamite)

const SMSOptIn = ({prefix = ''}) => {
    const {
        mutate: updateAttentiveSMS,
        isLoading,
        isSuccess
    } = useShopperCustomersMutation('updateCustomer')
    const customerId = useCustomerId()
    const {data: customer} = useCustomer({parameters: {customerId}}, {enabled: !!customerId})
    const {formatMessage} = useIntl()
    const {isGarage, brandId} = useBrand()
    const {privacyPolicyPath, termsAndConditionsPath} = useLegalPagePaths()
    const smsOptInKey = getSmsOptInKey(isGarage)
    const isSubscribed = Boolean(customer?.[smsOptInKey]) || isSuccess
    const brandName = brandId ? brandId.charAt(0).toUpperCase() + brandId.slice(1) : 'Dynamite'
    const [tempPhone, setTempPhone] = useState('')

    const maskPhone = (phone = '') => {
        const clean = phone.replace(/\D/g, '')

        if (clean.length <= 4) return clean
        const last4 = clean.slice(-4)
        return `(···) ··· - ${last4}`
    }

    const form = useForm({
        defaultValues: {
            [`${prefix}smsPhoneNumber`]: ''
        }
    })

    const fields = useCommunicationsFields({form, prefix})

    const onSubmit = ({smsPhoneNumber}) => {
        updateAttentiveSMS(
            {
                parameters: {
                    customerId
                },
                body: {
                    c_smsOptIn: {optedIn: true, brand: brandId, phone: smsPhoneNumber}
                }
            },
            {
                onError() {
                    // GTM Modification
                    sendViewErrorEvent(
                        'Failed to subscribe to SMS notifications. Please try again.'
                    )
                    form.setError('global', {
                        type: 'manual',
                        message: isGarage
                            ? formatMessage({
                                  id: 'communication_preferences.sms.error_message.garage',
                                  defaultMessage:
                                      'Failed to subscribe to SMS notifications. Please try again.'
                              })
                            : formatMessage({
                                  id: 'communication_preferences.sms.error_message.dynamite',
                                  defaultMessage:
                                      'Failed to subscribe to SMS notifications. Please try again.'
                              })
                    })
                },
                onSuccess() {
                    form.reset()
                    setTempPhone(smsPhoneNumber)
                }
            }
        )
    }

    return (
        <Box
            data-testid="communication-sms-section"
            display={'flex'}
            flexDirection={'column'}
            gap={'16px'}
            width={{base: 'full', lg: '458px'}}
            marginTop={{base: '48px', lg: '64px'}}
            marginBottom={{base: '20px', lg: '0'}}
        >
            <Heading
                as="h3"
                fontSize={{base: 'xs', lg: 'sm'}}
                fontWeight={'700'}
                textTransform={'uppercase'}
            >
                <FormattedMessage
                    id="communication_preferences.sms.title"
                    defaultMessage="SMS Notifications"
                />
            </Heading>
            {isSubscribed ? (
                <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                    <Flex gap={'5px'}>
                        <CheckCircleIcon boxSize={'20px'} />
                        <Text fontSize={'sm'} fontWeight={'400'}>
                            {tempPhone ? (
                                <>
                                    <FormattedMessage
                                        id="account_detail.subscribed_to_label"
                                        defaultMessage="Subscribed to"
                                    />
                                    {maskPhone(tempPhone)}
                                </>
                            ) : (
                                <FormattedMessage
                                    id="account_detail.subscribed_label"
                                    defaultMessage="Subscribed"
                                />
                            )}
                        </Text>
                    </Flex>
                    <Text fontSize={'xs'} ml={'3px'} fontWeight={'400'} color={'gray.700'}>
                        <FormattedMessage
                            id="account_detail.sms_unsubscribe_disclaimer"
                            defaultMessage="You can unsubscribe at any time by replying STOP to a text message. Message frequency varies. Standard message & data rates may apply."
                        />
                    </Text>
                </Box>
            ) : (
                <form id="sms-subscription-form" onSubmit={form.handleSubmit(onSubmit)}>
                    <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                        <Flex flexDirection={'column'} gap={'20px'}>
                            {isGarage ? (
                                <Text fontSize="sm" fontWeight={'400'}>
                                    <FormattedMessage
                                        id="communication_preferences.sms.description.garage"
                                        defaultMessage="Get special offers, early access to drops, and exclusive updates by text."
                                    />
                                </Text>
                            ) : (
                                <Text fontSize="sm" fontWeight={'400'}>
                                    <FormattedMessage
                                        id="communication_preferences.sms.description.dynamite"
                                        defaultMessage="Get special offers, early access to drops, and exclusive updates by text."
                                    />
                                </Text>
                            )}
                            <Text fontSize="sm" fontWeight={'700'}>
                                {isGarage ? (
                                    <FormattedMessage
                                        id="communication_preferences.earn_bonus_points_on_subscribe.garage"
                                        defaultMessage="Earn 10 bonus points when you subscribe."
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="communication_preferences.earn_bonus_points_on_subscribe.dynamite"
                                        defaultMessage="Earn 10 bonus points when you subscribe."
                                    />
                                )}
                            </Text>
                        </Flex>
                        <Box display={'flex'} flexDirection={'row'} gap={'10px'}>
                            <Field {...fields.smsPhoneNumber} />
                            <Button
                                variant="solid"
                                width={'48px'}
                                height={'48px'}
                                type="submit"
                                isLoading={isLoading}
                                onClick={() => {
                                    form.clearErrors('global')
                                }}
                            >
                                <RightArrowWhite />
                            </Button>
                        </Box>

                        <Text fontSize="xs" fontWeight={'400'} color={'gray.700'}>
                            {isGarage ? (
                                <FormattedMessage
                                    id="communication_preferences.sms.disclaimer.garage"
                                    defaultMessage="By signing up for SMS, you agree to receive promotional text messages from {brandName}. Message and data rates may apply. Message frequency varies. Reply STOP to unsubscribe. See {privacyLink} and {termsLink}."
                                    values={{
                                        brandName,
                                        termsLink: (
                                            <Link
                                                href={termsAndConditionsPath}
                                                textDecoration="underline"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Terms"
                                                    id="login_form.link.terms"
                                                />
                                            </Link>
                                        ),
                                        privacyLink: (
                                            <Link
                                                href={privacyPolicyPath}
                                                textDecoration="underline"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Privacy Policy"
                                                    id="login_form.link.privacy_policy"
                                                />
                                            </Link>
                                        )
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="communication_preferences.sms.disclaimer.dynamite"
                                    defaultMessage="By signing up for SMS, you agree to receive promotional text messages from {brandName}. Message and data rates may apply. Message frequency varies. Reply STOP to unsubscribe. See {privacyLink} and {termsLink}."
                                    values={{
                                        brandName,
                                        termsLink: (
                                            <Link
                                                href={termsAndConditionsPath}
                                                textDecoration="underline"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Terms"
                                                    id="login_form.link.terms"
                                                />
                                            </Link>
                                        ),
                                        privacyLink: (
                                            <Link
                                                href={privacyPolicyPath}
                                                textDecoration="underline"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Privacy Policy"
                                                    id="login_form.link.privacy_policy"
                                                />
                                            </Link>
                                        )
                                    }}
                                />
                            )}
                        </Text>
                    </Box>
                </form>
            )}
        </Box>
    )
}

export default SMSOptIn
